import React, { useEffect, useState, useMemo } from "react";
import {
    X, Plane, AlertTriangle, Truck, Tag, Gift, Sparkles, Megaphone,
    Calendar, Package, Clock, Headset, ArrowRight, Zap, Shield,
} from "lucide-react";
import api from "../lib/api";

const ICON_MAP = {
    plane: Plane, alert: AlertTriangle, warning: AlertTriangle, truck: Truck,
    sale: Tag, tag: Tag, gift: Gift, sparkles: Sparkles, megaphone: Megaphone,
    calendar: Calendar, package: Package, clock: Clock, headset: Headset,
    zap: Zap, shield: Shield,
};

const iconOf = (name) => ICON_MAP[name?.toLowerCase?.()] || Megaphone;

const dismissKey = (id) => `vortex_ann_dismissed_${id}`;
const isDismissed = (ann) => {
    if (!ann?.dismissible) return false;
    try {
        const raw = localStorage.getItem(dismissKey(ann.id));
        if (!raw) return false;
        const ts = parseInt(raw, 10);
        if (!ann.remember_days || ann.remember_days <= 0) return true; // dismiss forever
        return Date.now() - ts < ann.remember_days * 86400000;
    } catch {
        return false;
    }
};

const useCountdown = (target) => {
    const [ms, setMs] = useState(() => target ? (new Date(target).getTime() - Date.now()) : 0);
    useEffect(() => {
        if (!target) return;
        const id = setInterval(() => setMs(new Date(target).getTime() - Date.now()), 1000);
        return () => clearInterval(id);
    }, [target]);
    if (!target || ms <= 0) return null;
    const s = Math.floor(ms / 1000);
    return {
        days: Math.floor(s / 86400),
        hours: Math.floor((s % 86400) / 3600),
        minutes: Math.floor((s % 3600) / 60),
        seconds: s % 60,
    };
};

/**
 * Announcement banner — fetches the active announcement and renders in one of
 * four styles. Handles dismissal + view/click/dismiss analytics.
 *
 * `previewData` prop lets the admin preview panel render a draft without hitting the API.
 */
export const AnnouncementBanner = ({ previewData = null }) => {
    const [ann, setAnn] = useState(previewData);
    const [hidden, setHidden] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        if (previewData) {
            setAnn(previewData);
            setHidden(false);
            return;
        }
        api.get("/announcement").then((r) => {
            const d = r.data;
            if (!d) return;
            if (isDismissed(d)) return;
            setAnn(d);
            // fire view analytic (best-effort)
            api.post(`/announcement/${d.id}/view`).catch(() => {});
        }).catch(() => {});
    }, [previewData]);

    useEffect(() => {
        if (ann) setTimeout(() => setMounted(true), 30);
    }, [ann]);

    if (!ann || hidden) return null;

    const handleDismiss = () => {
        if (!ann.dismissible && !previewData) return;
        setHidden(true);
        if (previewData) return;
        try { localStorage.setItem(dismissKey(ann.id), String(Date.now())); } catch { /* localStorage may be blocked in privacy mode */ }
        api.post(`/announcement/${ann.id}/dismiss`).catch(() => {});
    };

    const handleClick = (e) => {
        if (previewData) { e.preventDefault(); return; }
        if (!ann.cta_url) { e.preventDefault(); return; }
        api.post(`/announcement/${ann.id}/click`).catch(() => {});
    };

    const style = ann.style || "card";
    // Carbon Labs rebrand — every announcement now renders as a slim
    // carbon-fibre rail so it stops dominating the fold. Modal-style
    // announcements still open as a modal because they aren't ambient
    // decoration (they interrupt the flow intentionally).
    if (style === "modal") return <ModalStyle ann={ann} mounted={mounted} onDismiss={handleDismiss} onClick={handleClick} />;
    return <BarStyle ann={ann} mounted={mounted} onDismiss={handleDismiss} onClick={handleClick} />;
};

// ---------- Card style (matches the user's reference image) ----------
const CardStyle = ({ ann, mounted, onDismiss, onClick }) => {
    const Icon = iconOf(ann.icon);
    // Carbon Labs rebrand — force cyan accent regardless of any legacy
    // gold value stored in the announcement DB record. This is a visual
    // override only; the admin panel can still edit content.
    const accent = "#00E5FF"; // Carbon Labs cyan — hex so `${accent}44` alpha suffixes remain valid
    const bg = ann.bg_color || "#0a0905";
    const countdown = useCountdown(ann.countdown_at);
    const tiles = ann.info_tiles || [];
    // In admin preview the banner is rendered inside a modal panel — skip the
    // fixed-header offset so it doesn't push down inside its container.
    const isPreview = ann.id === "preview";

    return (
        <div
            className="container-x"
            style={{ paddingTop: isPreview ? "1rem" : "1.5rem" }}
            data-testid="announcement-card"
        >
            <div
                className={`relative rounded-3xl overflow-hidden transition-all duration-700 ${
                    mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
                }`}
                style={{
                    background: bg,
                    border: `1px solid ${accent}55`,
                    boxShadow: `0 0 50px ${accent}30, inset 0 0 24px ${accent}0F`,
                }}
            >
                {ann.dismissible && (
                    <button
                        onClick={onDismiss}
                        className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-black/40 border border-white/15 flex items-center justify-center hover:bg-black/70"
                        data-testid="announcement-dismiss"
                        aria-label="Dismiss"
                    >
                        <X size={16} className="text-white/80" />
                    </button>
                )}
                <div className="p-5 sm:p-8">
                    {ann.badge_text && (
                        <div className="flex justify-center mb-5">
                            <div
                                className="inline-flex items-center gap-3 px-6 py-1.5 rounded-full text-[10px] sm:text-xs font-mono font-bold uppercase tracking-[0.35em]"
                                style={{
                                    border: `1px solid ${accent}66`,
                                    color: accent,
                                    background: `linear-gradient(90deg, transparent, ${accent}0F, transparent)`,
                                }}
                            >
                                <span className="w-4 h-px" style={{ background: accent }} />
                                {ann.badge_text}
                                <span className="w-4 h-px" style={{ background: accent }} />
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-8">
                        <div
                            className="shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center vip-icon-pulse"
                            style={{
                                background: `radial-gradient(circle, ${accent}44, transparent 70%)`,
                                border: `1px solid ${accent}`,
                            }}
                        >
                            <Icon size={30} style={{ color: accent }} />
                        </div>
                        <div className="flex-1 text-center sm:text-left min-w-0">
                            {ann.headline && (
                                <h2
                                    className="font-display font-black uppercase leading-none tracking-tight text-white"
                                    style={{ fontSize: "clamp(1.75rem, 5vw, 3rem)" }}
                                    data-testid="announcement-headline"
                                >
                                    {ann.headline}
                                </h2>
                            )}
                            {ann.subhead && (
                                <p
                                    className="font-display font-black leading-none tracking-tight mt-1"
                                    style={{ color: accent, fontSize: "clamp(1.25rem, 3.8vw, 2rem)" }}
                                    data-testid="announcement-subhead"
                                >
                                    {ann.subhead}
                                </p>
                            )}
                            {ann.description && (
                                <p className="text-sm sm:text-base text-white/70 mt-3 leading-relaxed" data-testid="announcement-description">
                                    {ann.description}
                                </p>
                            )}
                        </div>
                    </div>

                    {countdown && (
                        <div className="grid grid-cols-4 gap-2 max-w-md mx-auto mt-6" data-testid="announcement-countdown">
                            {[
                                { l: "Days", v: countdown.days },
                                { l: "Hours", v: countdown.hours },
                                { l: "Min", v: countdown.minutes },
                                { l: "Sec", v: countdown.seconds },
                            ].map((c) => (
                                <div key={c.l} className="text-center p-3 rounded-xl border border-white/10 bg-black/30">
                                    <p className="font-mono text-2xl font-black text-white tabular-nums">{String(c.v).padStart(2, "0")}</p>
                                    <p className="text-[9px] uppercase tracking-widest text-white/50 font-mono">{c.l}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {tiles.length > 0 && (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6 pt-6 border-t border-white/5">
                            {tiles.slice(0, 4).map((t, i) => {
                                const TIcon = iconOf(t.icon);
                                return (
                                    <div key={i} className="flex items-center gap-3" data-testid={`announcement-tile-${i}`}>
                                        <div
                                            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                                            style={{ background: `${accent}18`, border: `1px solid ${accent}44` }}
                                        >
                                            <TIcon size={16} style={{ color: accent }} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[9px] font-mono uppercase tracking-widest text-white/50 truncate">{t.label}</p>
                                            <p className="text-xs sm:text-sm font-mono font-bold text-white truncate" style={{ color: accent }}>{t.value}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {ann.cta_text && ann.cta_url && (
                        <div className="mt-6 flex justify-center">
                            <a
                                href={ann.cta_url}
                                onClick={onClick}
                                className="btn-solid inline-flex"
                                data-testid="announcement-cta"
                                style={{ background: `linear-gradient(135deg, ${accent}, ${accent}CC)`, color: "#0a0905" }}
                            >
                                {ann.cta_text} <ArrowRight size={14} />
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ---------- Sticky rail style (Carbon Labs compact banner) ----------
const BarStyle = ({ ann, mounted, onDismiss, onClick }) => {
    const Icon = iconOf(ann.icon);
    const accent = "#00E5FF";
    const isPreview = ann.id === "preview";
    return (
        <div
            className={`relative w-full transition-all duration-500 ${
                mounted ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
            }`}
            style={{
                background:
                    "linear-gradient(180deg, rgba(16,18,20,0.94) 0%, rgba(8,9,10,0.92) 100%), " +
                    "repeating-linear-gradient(45deg, rgba(255,255,255,0.024) 0 2px, rgba(0,0,0,0.35) 2px 4px)",
                backgroundBlendMode: "normal, overlay",
                borderTop: `1px solid rgba(0, 229, 255, 0.18)`,
                borderBottom: `1px solid rgba(0, 229, 255, 0.18)`,
                marginTop: 0,
            }}
            data-testid="announcement-bar"
        >
            {/* Cyan hairline glow along the bottom edge */}
            <span
                aria-hidden="true"
                className="absolute left-0 right-0 -bottom-px h-px"
                style={{
                    background: "linear-gradient(90deg, transparent, var(--cyan) 50%, transparent)",
                    boxShadow: "0 0 8px rgba(0,229,255,0.5)",
                    opacity: 0.7,
                }}
            />
            <div className="container-x py-2 flex items-center gap-3 relative">
                {/* Left corner ticks */}
                <span aria-hidden="true" className="absolute top-1 left-0 w-3 h-2 border-l border-t border-[var(--cyan)] opacity-70" />
                <span aria-hidden="true" className="absolute bottom-1 left-0 w-3 h-2 border-l border-b border-[var(--cyan)] opacity-70" />
                {/* Right corner ticks */}
                <span aria-hidden="true" className="absolute top-1 right-0 w-3 h-2 border-r border-t border-[var(--cyan)] opacity-70" />
                <span aria-hidden="true" className="absolute bottom-1 right-0 w-3 h-2 border-r border-b border-[var(--cyan)] opacity-70" />

                <span
                    aria-hidden="true"
                    className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: accent, boxShadow: `0 0 8px ${accent}` }}
                />
                {ann.badge_text && (
                    <span
                        className="hidden sm:inline-block font-mono uppercase text-[9px] tracking-[0.32em] shrink-0"
                        style={{ color: accent }}
                        data-testid="announcement-badge-text"
                    >
                        {ann.badge_text}
                    </span>
                )}
                {ann.badge_text && (
                    <span className="hidden sm:inline-block h-4 w-px bg-[rgba(0,229,255,0.25)] shrink-0" aria-hidden="true" />
                )}
                <Icon size={13} style={{ color: accent }} className="shrink-0" />
                <p className="text-[11px] sm:text-xs text-white/85 flex-1 truncate leading-tight">
                    {ann.headline && (
                        <span
                            className="mr-2 font-display uppercase tracking-[0.14em] font-bold"
                            style={{ color: accent }}
                        >
                            {ann.headline}
                        </span>
                    )}
                    {ann.subhead && (
                        <span className="mr-2 font-mono uppercase tracking-widest text-white/70 text-[10px] sm:text-[11px]">
                            {ann.subhead}
                        </span>
                    )}
                    {ann.description && (
                        <span className="text-white/60">{ann.description}</span>
                    )}
                </p>
                {ann.cta_text && ann.cta_url && (
                    <a
                        href={ann.cta_url}
                        onClick={onClick}
                        className="hidden md:inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.22em] font-bold whitespace-nowrap shrink-0 hover:underline"
                        style={{ color: accent }}
                        data-testid="announcement-cta"
                    >
                        {ann.cta_text} <ArrowRight size={11} />
                    </a>
                )}
                {ann.dismissible && (
                    <button
                        onClick={onDismiss}
                        className="w-6 h-6 rounded-sm border border-white/10 hover:border-[var(--cyan)] hover:bg-[rgba(0,229,255,0.08)] flex items-center justify-center shrink-0 transition-colors"
                        data-testid="announcement-dismiss"
                        aria-label="Dismiss"
                    >
                        <X size={11} className="text-white/70" />
                    </button>
                )}
            </div>
        </div>
    );
};

// ---------- Modal (popup) style ----------
const ModalStyle = ({ ann, mounted, onDismiss, onClick }) => {
    const Icon = iconOf(ann.icon);
    const accent = "#00E5FF"; // Carbon Labs cyan — hex so `${accent}44` alpha suffixes remain valid
    return (
        <div
            className={`fixed inset-0 z-[90] flex items-center justify-center p-4 transition-opacity duration-500 ${
                mounted ? "opacity-100" : "opacity-0"
            }`}
            style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
            onClick={ann.dismissible ? onDismiss : undefined}
            data-testid="announcement-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className={`relative max-w-md w-full rounded-3xl overflow-hidden transition-all duration-500 ${
                    mounted ? "scale-100" : "scale-95"
                }`}
                style={{
                    background: ann.bg_color || "#0a0905",
                    border: `1px solid ${accent}66`,
                    boxShadow: `0 0 80px ${accent}66`,
                }}
            >
                {ann.dismissible && (
                    <button onClick={onDismiss} className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/40 border border-white/15 flex items-center justify-center hover:bg-black/70" data-testid="announcement-dismiss" aria-label="Dismiss">
                        <X size={16} className="text-white/80" />
                    </button>
                )}
                <div className="p-7 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}>
                        <Icon size={26} style={{ color: accent }} />
                    </div>
                    {ann.badge_text && (
                        <p className="text-[10px] font-mono uppercase tracking-[0.35em] mb-2" style={{ color: accent }}>{ann.badge_text}</p>
                    )}
                    {ann.headline && <h2 className="font-display font-black text-white text-2xl sm:text-3xl mb-1" data-testid="announcement-headline">{ann.headline}</h2>}
                    {ann.subhead && <p className="font-display font-bold text-lg mb-3" style={{ color: accent }}>{ann.subhead}</p>}
                    {ann.description && <p className="text-sm text-white/70 mb-5" data-testid="announcement-description">{ann.description}</p>}
                    {ann.cta_text && ann.cta_url && (
                        <a href={ann.cta_url} onClick={onClick} className="btn-solid inline-flex" style={{ background: accent, color: "#0a0905" }} data-testid="announcement-cta">
                            {ann.cta_text} <ArrowRight size={14} />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

// ---------- Full-width hero style ----------
const HeroStyle = ({ ann, mounted, onDismiss, onClick }) => {
    const Icon = iconOf(ann.icon);
    const accent = "#00E5FF"; // Carbon Labs cyan — hex so `${accent}44` alpha suffixes remain valid
    const isPreview = ann.id === "preview";
    return (
        <div
            className={`relative w-full transition-all duration-700 ${
                mounted ? "opacity-100" : "opacity-0"
            }`}
            style={{
                background: ann.bg_color || `radial-gradient(circle at 30% 40%, ${accent}22, #0a0905 60%)`,
                borderBottom: `1px solid ${accent}55`,
                minHeight: "260px",
                paddingTop: 0,
            }}
            data-testid="announcement-hero"
        >
            {ann.dismissible && (
                <button onClick={onDismiss} className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-black/40 border border-white/15 flex items-center justify-center hover:bg-black/70" data-testid="announcement-dismiss" aria-label="Dismiss">
                    <X size={16} className="text-white/80" />
                </button>
            )}
            <div className="container-x py-12 sm:py-16 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}>
                    <Icon size={26} style={{ color: accent }} />
                </div>
                {ann.badge_text && (
                    <p className="text-[10px] font-mono uppercase tracking-[0.4em] mb-3" style={{ color: accent }}>{ann.badge_text}</p>
                )}
                {ann.headline && <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-none" data-testid="announcement-headline">{ann.headline}</h1>}
                {ann.subhead && <p className="font-display font-black text-2xl sm:text-3xl mt-2" style={{ color: accent }}>{ann.subhead}</p>}
                {ann.description && <p className="max-w-2xl mx-auto text-sm sm:text-base text-white/70 mt-4" data-testid="announcement-description">{ann.description}</p>}
                {ann.cta_text && ann.cta_url && (
                    <a href={ann.cta_url} onClick={onClick} className="btn-solid inline-flex mt-6" style={{ background: accent, color: "#0a0905" }} data-testid="announcement-cta">
                        {ann.cta_text} <ArrowRight size={14} />
                    </a>
                )}
            </div>
        </div>
    );
};

